import { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon, Email, Phone, Facebook, Instagram, PhoneAndroid } from '@mui/icons-material';
import MusicNoteIcon from '@mui/icons-material/MusicNote'; // TikTok replacement
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const menuItems = [
    { title: 'Home', path: '/' },
    { title: 'Projects', path: '/projects' },
    { title: 'Services', path: '/services' },
    { title: 'About Us', path: '/about' },
    { title: 'Contact Us', path: '/contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', backgroundColor: '#F9F7F7', height: '100%', color: '#333' }}>
      <Box sx={{ py: 4, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <Box
          component="img"
          src="/logo/logo.png"
          alt="All In One Construction"
          sx={{
            height: 60,
            width: 'auto',
            mt: 3,
          }}
        />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: '#2C3E50',
            letterSpacing: '-0.8px',
            fontSize: '1.4rem',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          }}
        >
          ALL IN ONE
        </Typography>
      </Box>
      <List sx={{ px: 2, pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.title} disablePadding sx={{ mb: 1 }}>
            <Button
              component={Link}
              to={item.path}
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '8px',
                color: location.pathname === item.path ? '#2C3E50' : '#5D6D7E',
                backgroundColor: location.pathname === item.path ? 'rgba(52, 152, 219, 0.1)' : 'transparent',
                fontWeight: location.pathname === item.path ? 600 : 400,
                fontSize: '1rem',
                textTransform: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(52, 152, 219, 0.1)',
                  color: '#2C3E50',
                },
              }}
            >
              <ListItemText primary={item.title} />
            </Button>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // Top header with contact info and social icons

  const TopHeader = () => {
    const [showContact, setShowContact] = useState(false);
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.only('xs'));

    return (
      <Box sx={{
        width: '100%',
        background: '#0169B3',
        color: '#FDFAF6',
        fontSize: { xs: '0.85rem', sm: '0.95rem' },
        px: { xs: 1, sm: 4 },
        py: { xs: 1, sm: 0.5 },
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: { xs: 'right', sm: 'flex-end' },
        gap: { xs: 2, sm: 4 },
        borderBottom: '1px solid #e0e0e0',
        minHeight: 36,
        zIndex: 1300,
        textAlign: { xs: 'center', sm: 'right' },
        position: 'relative',
      }}>
        {/* Social Media Icons - always visible */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 4 } }}>
          <a
            href="https://www.facebook.com/profile.php?id=61578199235565&rdid=bUK35ypmxLQoax68&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F19Aq8AFVUS%2F#"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#FDFAF6', display: 'flex', transition: 'color 0.2s, transform 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#1877F2'; e.currentTarget.style.transform = 'scale(1.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#FDFAF6'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <Facebook sx={{ fontSize: 25 }} />
          </a>
          <a
            href="https://www.instagram.com/all.inoneconstruction/#"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#FDFAF6', display: 'flex', transition: 'color 0.2s, transform 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#E1306C'; e.currentTarget.style.transform = 'scale(1.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#FDFAF6'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <Instagram sx={{ fontSize: 25 }} />
          </a>
          <a
            href="https://www.tiktok.com/@all.in.one.constru"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#FDFAF6', display: 'flex', transition: 'color 0.2s, transform 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#000000'; e.currentTarget.style.transform = 'scale(1.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#FDFAF6'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <MusicNoteIcon sx={{ fontSize: 25 }} />
          </a>
          {/* <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" style={{ color: '#FDFAF6', display: 'flex' }}>
            <LinkedIn sx={{ fontSize: 25 }} />
          </a> */}
        </Box>
        {/* Contact Icons - always on same line */}
        {isXs ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, }}>
            <IconButton size="small" sx={{ color: '#FDFAF6' }} onClick={() => setShowContact((v) => !v)}>
              <Email sx={{ fontSize: 22 }} />
            </IconButton>
            <IconButton size="small" sx={{ color: '#FDFAF6' }} onClick={() => setShowContact((v) => !v)}>
              <Phone sx={{ fontSize: 22 }} />
            </IconButton>
            {showContact && (
              <Box sx={{
                position: 'absolute',
                top: 40,
                left: 0,
                width: '100vw',
                background: '#0169B3',
                color: '#FDFAF6',
                py: 2,
                zIndex: 2000,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 18, color: '#FDFAF6' }} />
                  <a href="mailto:info@allinoneconstructions.com" style={{ color: '#FDFAF6', textDecoration: 'none', fontWeight: 500 }}>
                    info@allinoneconstructions.com
                  </a>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneAndroid sx={{ fontSize: 18, color: '#FDFAF6' }} />
                  <a href="tel:+447931937835" style={{ color: '#FDFAF6', textDecoration: 'none', fontWeight: 500 }}>
                    +44 7931 937835
                  </a>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 18, color: '#FDFAF6' }} />
                  <a href="tel:+442035356266" style={{ color: '#FDFAF6', textDecoration: 'none', fontWeight: 500 }}>
                    020 3535 6266
                  </a>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <Email sx={{ fontSize: 18, color: '#FDFAF6' }} />
              <a href="mailto:info@allinoneconstructions.com" style={{ color: '#FDFAF6', textDecoration: 'none', fontWeight: 500 }}>
                info@allinoneconstructions.com
              </a>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <PhoneAndroid sx={{ fontSize: 18, color: '#FDFAF6' }} />
              <a href="tel:+447931937835" style={{ color: '#FDFAF6', textDecoration: 'none', fontWeight: 500 }}>
                +44 7931 937835
              </a>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone sx={{ fontSize: 18, color: '#FDFAF6' }} />
              <a href="tel:+442035356266" style={{ color: '#FDFAF6', textDecoration: 'none', fontWeight: 500 }}>
                020 3535 6266
              </a>
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <>
      <TopHeader />
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: '#F9F7F7',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          color: '#333333',
          height: '70px',
          zIndex: 1200, // Higher z-index to ensure it stays above slider
          '& .MuiToolbar-root': {
            height: '90px',
            minHeight: '90px !important',
          }
        }}
      >
        <Toolbar sx={{ py: 0, height: '90px', minHeight: '90px !important', justifyContent: 'space-between', position: 'relative' }}>
          {/* Logo - Left aligned with overlap effect */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1201, // Above slider but below navbar
            height: '70px'
          }}>
            <Box
              component="img"
              src="/logo/logo.png"
              alt="All In One Construction"
              sx={{
                height: { xs: 115, sm: 125, md: 135, lg: 145 }, // Much larger logo
                width: 'auto',
                // mr: 1,
                mt: { xs: 0, md: 0 },
                filter: 'brightness(1) contrast(1.1) drop-shadow(0 4px 12px rgba(0,0,0,0.1))',
                transition: 'all 0.3s ease',
                position: 'relative',
                zIndex: 1202,
                transform: 'translateY(25px)', // Push logo down to overlap slider
                '&:hover': {
                  transform: 'translateY(25px) scale(1.05)',
                  filter: 'brightness(1.1) contrast(1.2) drop-shadow(0 6px 16px rgba(0,0,0,0.15))',
                },
              }}
            />
            <Typography
              variant="h3"
              component={Link}
              to="/"
              sx={{
                fontWeight: 600,
                textDecoration: 'none',
                color: '#2C3E50',
                display: 'block', // Always show, even on xs
                letterSpacing: '-0.8px',
                fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.5rem', lg: '1.8rem' },
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                transition: 'all 0.3s ease',
                position: 'relative',
                zIndex: 1202,
                transform: 'translateY(8px)',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  color: '#34495E',
                  transform: 'translateY(25px) scale(1.02)',
                  textShadow: '0 3px 6px rgba(0,0,0,0.15)',
                },
              }}
            >
              ALL IN ONE
            </Typography>
          </Box>

          {/* Desktop Navigation - Right aligned */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.title}
                  component={Link}
                  to={item.path}
                  sx={{
                    mx: 1,
                    px: 2,
                    py: 1,
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    fontSize: '0.95rem',
                    letterSpacing: '0.3px',
                    color: location.pathname === item.path ? '#2C3E50' : '#5D6D7E',
                    textTransform: 'none',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: location.pathname === item.path ? '100%' : '0%',
                      height: 2,
                      backgroundColor: '#3498DB',
                      transition: 'width 0.3s ease',
                    },
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#2C3E50',
                      '&::after': {
                        width: '100%',
                      },
                    },
                  }}
                >
                  {item.title}
                </Button>
              ))}
            </Box>
          )}

          {/* Mobile menu button - Right aligned */}
          {isMobile && (
            <IconButton
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{
                color: '#5D6D7E',
                ml: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(52, 152, 219, 0.1)',
                  color: '#2C3E50',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            backgroundColor: '#F9F7F7',
            borderLeft: '1px solid rgba(0,0,0,0.08)',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;